import { useState } from 'react';
import { Clock, Star, MapPin, Calendar, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TravelAgency } from '../types';
import NearbyAgenciesModal from './NearbyAgenciesModal';
import ImageWithFallback from './ImageWithFallback';
import AirlineBadge from './AirlineBadge';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { parseFlexibleDate } from '../utils/dateParse';
import { getCategoryLabel } from '../utils/categoryLabel';

interface PackageProps {
  id: number;
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: string;
  type: 'B2B' | 'B2C';
  departure: string;
  departures: {
    id: number;
    departureDate: string;
    returnDate: string;
  }[];
  formulas: {
    id: number;
    name: string;
    priceType: string;
    perPersonPrice: number | null;
    roomPrices: {
      single: number;
      double: number;
      triple: number;
      quadruple: number;
      quintuple: number;
    };
  }[];
  agency?: {
    id: number;
    name: string;
    logo: string;
    rating?: number;
    verified: boolean;
    /** Couleur de fond du bloc logo (ex. theme.secondaryColor), comme sur la liste des agences */
    secondaryColor?: string | null;
  };
  airline?: {
    id: number;
    name: string;
    logo: string;
    code?: string | null;
  } | null;
}

interface PackageCardProps {
  package: PackageProps;
  primaryColor: string;
  isBookingPage?: boolean;
  /** Liste type comparateur (image à gauche, détails à droite) */
  layout?: 'vertical' | 'horizontal';
}

export default function PackageCard({
  package: pkg,
  primaryColor,
  isBookingPage,
  layout = 'vertical',
}: PackageCardProps) {
  const isHorizontal = layout === 'horizontal';
  const [showAgenciesModal, setShowAgenciesModal] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);

  const departures = Array.isArray(pkg.departures) ? pkg.departures : [];

  const getDisplayPrice = (formula: PackageProps['formulas'][0]) => {
    if (formula.priceType === 'PER_PERSON' && formula.perPersonPrice) {
      return formula.perPersonPrice;
    }
    
    if (formula.roomPrices) {
      const prices = Object.values(formula.roomPrices).filter(price => price > 0);
      return prices.length > 0 ? Math.min(...prices) : 0;
    }

    return 0;
  };

  const lowestPrice = Array.isArray(pkg.formulas) && pkg.formulas.length > 0
    ? pkg.formulas.reduce((min, formula) => {
        const price = getDisplayPrice(formula);
        return Math.min(min, price);
      }, Infinity)
    : 0;

  // Obtenir la prochaine date de départ
  const getNextDeparture = () => {
    if (departures.length === 0) return null;

    const now = new Date();
    const futureDepartures = departures
      .filter((dep) => {
        const d = parseFlexibleDate(dep.departureDate);
        return !isNaN(d.getTime()) && d >= now;
      })
      .sort(
        (a, b) =>
          parseFlexibleDate(a.departureDate).getTime() -
          parseFlexibleDate(b.departureDate).getTime()
      );

    return futureDepartures[0] || departures[0];
  };

  const nextDeparture = getNextDeparture();

  // Calcul de la durée basé sur la prochaine date de départ
  const getDuration = () => {
    if (!nextDeparture) return { days: 0, nights: 0 };

    try {
      const departureDate = parseFlexibleDate(nextDeparture.departureDate);
      const returnDate = parseFlexibleDate(nextDeparture.returnDate);

      if (isNaN(departureDate.getTime()) || isNaN(returnDate.getTime())) {
        return { days: 0, nights: 0 };
      }

      const days = differenceInDays(returnDate, departureDate);
      return {
        days: Math.max(days, 0),
        nights: Math.max(days - 1, 0),
      };
    } catch (error) {
      console.error('Erreur de calcul de durée:', error);
      return { days: 0, nights: 0 };
    }
  };

  const { days: daysCount, nights: nightsCount } = getDuration();

  const isValidDate = (date: string | number | Date | null | undefined): boolean => {
    if (date == null || date === '') return false;
    const parsedDate = parseFlexibleDate(date);
    return !isNaN(parsedDate.getTime());
  };

  const formatDate = (date: string | number | Date | null | undefined): string => {
    if (!isValidDate(date)) {
      return 'Date à confirmer';
    }
    try {
      return format(parseFlexibleDate(date), 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return 'Date à confirmer';
    }
  };

  /** Format court pour la ligne unique (carte horizontale) */
  const formatDateCompact = (date: string | number | Date | null | undefined): string => {
    if (!isValidDate(date)) return '—';
    try {
      return format(parseFlexibleDate(date), 'dd/MM/yyyy');
    } catch {
      return '—';
    }
  };

  const durationLabel = (() => {
    if (departures.length === 0) return 'Aucune date de départ';
    if (
      nextDeparture &&
      isValidDate(nextDeparture.departureDate) &&
      isValidDate(nextDeparture.returnDate)
    ) {
      return `${daysCount} jour${daysCount !== 1 ? 's' : ''} / ${nightsCount} nuit${nightsCount !== 1 ? 's' : ''}`;
    }
    return 'Durée à confirmer';
  })();

  // Trier les dates de départ
  const sortedDepartures = [...departures].sort(
    (a, b) =>
      parseFlexibleDate(a.departureDate).getTime() -
      parseFlexibleDate(b.departureDate).getTime()
  );

  // Afficher soit toutes les dates, soit les N premières (moins en horizontal = carte moins haute)
  const displayedDepartures = showAllDates
    ? sortedDepartures
    : sortedDepartures.slice(0, isHorizontal ? 2 : 3);

  const imageBlock = (
    <div
      className={
        isHorizontal
          ? 'relative h-44 w-full shrink-0 overflow-hidden sm:h-full sm:min-h-[176px] sm:w-56 lg:w-64'
          : 'relative aspect-[3/2]'
      }
    >
      <ImageWithFallback
        src={pkg.image}
        alt={pkg.title}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        className={`absolute z-[1] rounded-full font-semibold text-white shadow ${
          isHorizontal ? 'right-2 top-2 px-2.5 py-1 text-xs' : 'right-3 top-3 px-3 py-1 text-sm'
        }`}
        style={{ backgroundColor: primaryColor }}
      >
        À partir de {lowestPrice.toLocaleString('fr-DZ')} DA
      </div>

      {!isHorizontal && pkg.agency && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <div className="flex items-center gap-3">
            {pkg.agency.logo ? (
              <div
                style={{
                  backgroundColor: pkg.agency.secondaryColor?.trim() || '#1E40AF',
                }}
                className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full p-2 shadow-md"
              >
                <ImageWithFallback
                  src={pkg.agency.logo}
                  alt={pkg.agency.name}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: pkg.agency.secondaryColor?.trim() || '#1E40AF',
                }}
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full shadow-md"
              >
                <Building className="h-8 w-8 text-white" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h4 className="font-medium text-white">{pkg.agency.name}</h4>
              <div className="flex items-center gap-2 text-white/90">
                <div className="flex items-center">
                  <Star className="h-4 w-4 fill-current text-yellow-400" />
                  <span className="ml-1 text-sm">{pkg.agency.rating}</span>
                </div>
                {pkg.agency.verified && (
                  <span
                    className="rounded-full border px-2 py-0.5 text-xs text-white"
                    style={{
                      backgroundColor: `${primaryColor}33`,
                      borderColor: `${primaryColor}4D`,
                    }}
                  >
                    Vérifié
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const moreDatesCount = isHorizontal
    ? Math.max(0, sortedDepartures.length - 2)
    : Math.max(0, sortedDepartures.length - 3);

  const metaBlock = (
    <div className="mb-4 space-y-4">
      <div>
        <div className="mb-2 flex items-center">
          <Calendar className="mr-2 h-4 w-4 shrink-0 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Dates de départ</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {displayedDepartures.map((departure) => (
            <span
              key={departure.id}
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: `${'#181105'}15`,
                color: '#181105',
                border: `1px solid ${primaryColor}30`,
              }}
            >
              {formatDate(departure.departureDate)}
            </span>
          ))}
          {moreDatesCount > 0 && !showAllDates && (
            <button
              type="button"
              onClick={() => setShowAllDates(true)}
              className="text-xs font-medium hover:underline"
              style={{ color: primaryColor }}
            >
              +{moreDatesCount} date{moreDatesCount > 1 ? 's' : ''}
            </button>
          )}
          {showAllDates && sortedDepartures.length > (isHorizontal ? 2 : 3) && (
            <button
              type="button"
              onClick={() => setShowAllDates(false)}
              className="text-xs font-medium hover:underline"
              style={{ color: primaryColor }}
            >
              Voir moins
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center text-sm text-gray-600">
        <Clock className="mr-2 h-4 w-4 shrink-0" />
        <span>{durationLabel}</span>
      </div>
      <div className="flex items-center text-sm text-gray-600">
        <MapPin className="mr-2 h-4 w-4 shrink-0" />
        <span>Départ de {pkg.departure}</span>
      </div>
    </div>
  );

  /** Catégorie + dates + durée + départ ; compagnie en bloc type liste séjours (carte horizontale) */
  const horizontalInfoLine = (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-1.5 text-sm text-gray-700">
        <span
          className="inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: `${primaryColor}18`,
            color: '#1f2937',
            border: `1px solid ${primaryColor}35`,
          }}
        >
          {getCategoryLabel(pkg.category)}
        </span>
        {pkg.type === 'B2B' && (
          <span className="inline-flex shrink-0 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
            B2B
          </span>
        )}
        <span className="text-gray-300" aria-hidden>
          ·
        </span>
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          {displayedDepartures.map((departure) => (
            <span
              key={departure.id}
              className="whitespace-nowrap rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-800"
            >
              {formatDateCompact(departure.departureDate)}
            </span>
          ))}
          {moreDatesCount > 0 && !showAllDates && (
            <button
              type="button"
              onClick={() => setShowAllDates(true)}
              className="text-xs font-semibold hover:underline"
              style={{ color: primaryColor }}
            >
              +{moreDatesCount}
            </button>
          )}
          {showAllDates && sortedDepartures.length > (isHorizontal ? 2 : 3) && (
            <button
              type="button"
              onClick={() => setShowAllDates(false)}
              className="text-xs font-medium hover:underline"
              style={{ color: primaryColor }}
            >
              −
            </button>
          )}
        </span>

        <span className="text-gray-300" aria-hidden>
          ·
        </span>
        <span className="inline-flex min-w-0 items-center gap-1 text-gray-600">
          <Clock className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          <span className="truncate">{durationLabel}</span>
        </span>

        <span className="text-gray-300" aria-hidden>
          ·
        </span>
        <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-gray-600">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          <span className="truncate">{pkg.departure}</span>
        </span>
      </div>
      {pkg.airline ? (
        <div className="mb-2">
          <AirlineBadge airline={pkg.airline} variant="labeled" />
        </div>
      ) : null}
    </>
  );

  return (
    <>
      <div
        className={`overflow-hidden bg-white shadow-md transition-shadow duration-300 hover:shadow-lg ${
          isHorizontal ? 'flex flex-col rounded-lg sm:flex-row sm:items-stretch' : 'rounded-xl shadow-lg hover:shadow-xl'
        }`}
      >
        {imageBlock}

        {/* Contenu */}
        <div
          className={`flex min-w-0 flex-1 flex-col ${isHorizontal ? 'p-2.5 sm:p-3' : 'p-6'}`}
        >
          {isHorizontal && pkg.agency && (
            <div className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-1.5">
              {pkg.agency.logo ? (
                <div
                  style={{
                    backgroundColor: pkg.agency.secondaryColor?.trim() || '#1E40AF',
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full p-1"
                >
                  <ImageWithFallback
                    src={pkg.agency.logo}
                    alt={pkg.agency.name}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: pkg.agency.secondaryColor?.trim() || '#1E40AF',
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                >
                  <Building className="h-5 w-5 text-white" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">{pkg.agency.name}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                  <span className="flex items-center">
                    <Star className="mr-0.5 h-3.5 w-3.5 fill-current text-amber-400" />
                    {pkg.agency.rating}
                  </span>
                  {pkg.agency.verified && (
                    <span
                      className="rounded border px-1.5 py-0.5 text-[11px] font-medium"
                      style={{
                        borderColor: `${primaryColor}55`,
                        color: primaryColor,
                      }}
                    >
                      Vérifié
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <h3
            className={`font-semibold text-gray-900 ${
              isHorizontal ? 'mb-1.5 text-base leading-snug md:text-lg' : 'mb-2 text-lg'
            }`}
          >
            {pkg.title}
          </h3>

          {isHorizontal ? (
            horizontalInfoLine
          ) : (
            <>
              <div className="mb-3">
                <span
                  className="inline-block rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{
                    backgroundColor: `${primaryColor}18`,
                    color: '#1f2937',
                    border: `1px solid ${primaryColor}35`,
                  }}
                >
                  {getCategoryLabel(pkg.category)}
                </span>
                {pkg.type === 'B2B' && (
                  <span className="ml-2 inline-block rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                    B2B
                  </span>
                )}
              </div>

              {pkg.airline ? (
                <div className="mb-3">
                  <AirlineBadge airline={pkg.airline} variant="labeled" />
                </div>
              ) : null}

              {metaBlock}
            </>
          )}

          <p
            className={`text-gray-600 ${isHorizontal ? 'mb-2 line-clamp-1 text-sm' : 'mb-6 line-clamp-2'}`}
          >
            {pkg.description}
          </p>

          <div
            className={`mt-auto flex ${isHorizontal ? 'flex-col gap-2 sm:flex-row sm:items-center' : 'gap-3'}`}
          >
            <Link
              to={`/package/${pkg.id}`}
              className={`text-center font-medium text-white transition hover:opacity-90 ${
                isHorizontal
                  ? 'flex-1 rounded-md py-2 text-sm sm:px-3'
                  : 'flex-1 rounded-lg px-4 py-2.5 text-sm'
              }`}
              style={{ backgroundColor: primaryColor }}
            >
              Voir les détails
            </Link>
            {isBookingPage ? (
              <Link
                to={`/admin/bookings/new/${pkg.id}/booking`}
                className={`bg-blue-600 text-center font-medium text-white transition hover:bg-blue-700 ${
                  isHorizontal
                    ? 'flex-1 rounded-md py-2 text-sm sm:px-3'
                    : 'flex-1 rounded-lg px-4 py-2.5 text-sm'
                }`}
              >
                Réserver ce package
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setShowAgenciesModal(true)}
                className={`flex items-center justify-center gap-2 rounded-md border text-sm transition hover:bg-gray-50 ${
                  isHorizontal ? 'px-3 py-2 sm:w-auto sm:shrink-0' : 'rounded-lg px-4 py-2.5'
                }`}
                style={{
                  borderColor: primaryColor,
                  color: primaryColor,
                }}
              >
                <MapPin className="h-4 w-4" />
                Réserver
              </button>
            )}
          </div>
        </div>
      </div>

      <NearbyAgenciesModal
        isOpen={showAgenciesModal}
        onClose={() => setShowAgenciesModal(false)}
        agencies={
          pkg.agency
            ? ([
                {
                  id: String(pkg.agency.id),
                  name: pkg.agency.name,
                  logo: pkg.agency.logo,
                  rating: pkg.agency.rating ?? 0,
                  reviewCount: 0,
                  verified: pkg.agency.verified,
                },
              ] satisfies TravelAgency[])
            : []
        }
      />
    </>
  );
}